import React, { useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { DateTime } from 'luxon';

// Components
import { Map, MapControls, RealtimeLayers } from 'src/@realtime/components';

// Hooks
import { useGetUrlDate, useMapConfigsWrapper, useIsHistoricalOnly } from 'src/@realtime/hooks';
import { useMapContext } from 'src/@realtime/contexts/map';
import { useConfigSelectors, useLanguageSelectors } from 'src/app/reducers';
import { useSetPlaybackMode } from 'src/@realtime/hooks';

// Utilities
import { setTabTitle } from 'src/utils';
import { displayToast } from '@ems/client-design-system';
import { Intent } from '@blueprintjs/core';

// Constants
import { DEFAULT_BBOX_DISTANCE } from 'src/constants';
import {
  PlaybackActionType,
  PlaybackMode,
  PlaybackStatus,
  usePlaybackContext,
} from 'src/@realtime/contexts';

export const RealtimePage: React.FC = () => {
  const configSelectors = useConfigSelectors();
  const {
    globals: { timeZone },
  } = configSelectors.getConfig();
  const { isHistoricalOnly, historicalMinsBuffer } = useIsHistoricalOnly();

  const {
    state: { isMapSettingsOpen },
  } = useMapContext();
  const {
    state: { isLive, playbackMode },
    dispatch: playbackDispatch,
  } = usePlaybackContext();

  // Pause playback when settings are opened.
  useEffect(() => {
    playbackDispatch({
      type: PlaybackActionType.SET_PLAYBACK_STATUS,
      payload: PlaybackStatus.Paused,
    });
    // Prevent playback skipping on due to catchup code
    if (isLive && playbackMode === PlaybackMode.Realtime) {
      playbackDispatch({
        type: PlaybackActionType.SET_IS_LIVE,
        payload: false,
      });
    }
  }, [isMapSettingsOpen]);

  const check = useSetPlaybackMode();
  const getUrlDate = useGetUrlDate();

  let dateToLoad: DateTime = getUrlDate();

  if (!dateToLoad && isHistoricalOnly) {
    // If historical only mode is enabled, set the date to the current time minus the historicalOnlyMinsBefore value + timeline length
    dateToLoad = DateTime.now()
      .setZone(timeZone)
      .minus({ minutes: historicalMinsBuffer });
  }

  const languageSelectors = useLanguageSelectors();
  const {
    screens: {
      realtime: {
        title: realtimePageTitle,
        messages: { webGLRequired },
      },
    },
  } = languageSelectors.getLanguage();

  const {
    centre: { latitude, longitude },
    zoom,
    mapAccessToken,
    maxZoom,
    minZoom,
  } = useMapConfigsWrapper();

  setTabTitle(realtimePageTitle);

  useEffect(() => {
    check(dateToLoad);

    // Check for WebGL support and notify user if not supported
    if (!mapboxgl.supported()) {
      displayToast({
        message: webGLRequired as string,
        intent: Intent.DANGER,
        timeout: 0, // Persistent
        icon: 'warning-sign',
      });
    }
  }, []);

  const {
    state: { isMapLoaded },
  } = useMapContext();

  return (
    <div style={{ width: '100%', height: '100vh' }} role="presentation">
      <Map
        hash
        pitch={0}
        bearing={0}
        latitude={latitude}
        longitude={longitude}
        zoom={zoom}
        apiKey={mapAccessToken}
        maxZoom={maxZoom}
        minZoom={minZoom}
        maxBoundsDistance={DEFAULT_BBOX_DISTANCE}>
        <MapControls resetView mapSettings measureTool />
        {isMapLoaded && <RealtimeLayers />}
      </Map>
    </div>
  );
};
